// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import styles from './CreateUpdateSiteGroupForm.module.scss';
import { InputDataListField } from '../InputDataListField';
import { ModalType } from 'util/Enums';
import { useTranslation } from 'react-i18next';
import { validateStringField } from 'util/validator';
import {
  OptionItem,
  SelectFieldMultipleChoise,
} from '../SelectFieldMultipleChoise';
import { createAxios } from 'ts/createAxios';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { handleError } from 'util/errorHandler';
import { SiteGroupCreateEditModalType } from 'components/organisms/CreateUpdateSiteGroupModal';
import { CommonSite } from 'util/Types';

interface Props {
  groupData: SiteGroupCreateEditModalType;
  onSubmit: (form: SiteGroupInput, groupId?: number) => void;
}

export type SiteGroupInput = {
  name: string;
  siteIds: string[];
};

const CreateUpdateSiteGroupForm: React.FC<Props> = ({
  groupData,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const api = createAxios();
  const [siteOptions, setSiteOptions] = useState<OptionItem[]>([]);

  const fetchSites = async () => {
    try {
      const { data } = await api.get<CommonSite[]>(`/collects/emission-sites`, {
        params: { siteId: user.siteId },
      });
      const sites: OptionItem[] = data
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setSiteOptions(sites);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  return (
    <div className={styles.createBody}>
      <Form<SiteGroupInput>
        keepDirtyOnReinitialize
        initialValues={
          groupData.group
            ? {
                name: groupData.group?.name,
                siteIds: groupData.group?.sites.map((item) => String(item.id)),
              }
            : {}
        }
        onSubmit={(values) => {
          if (groupData.type === ModalType.add) {
            onSubmit(values);
          } else {
            onSubmit(values, groupData.group?.id);
          }
        }}
        validate={(values) => {
          return {
            name: validateStringField(
              t('site_group_management.label_name'),
              100,
              values.name
            ),
          };
        }}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
      >
        {(props) => (
          <form onSubmit={props.handleSubmit} className={styles.formInput}>
            <div className={styles.inputFrame}>
              <InputDataListField
                label={t('site_group_management.label_name') || ''}
                placeholder={t('site_group_management.label_name') || ''}
                name="name"
                required
                maxlength={100}
              />
              <SelectFieldMultipleChoise
                options={siteOptions}
                label={t('site_group_management.label_site') || ''}
                placeholder={t('site_group_management.label_site') || ''}
                name="siteIds"
                searchable
                onChange={(sites) => {
                  props.form.mutators.setValue(
                    'siteIds',
                    sites.map((site) => site.id)
                  );
                }}
                selectedOptions={
                  siteOptions.filter((site) =>
                    props.values.siteIds?.includes(String(site.id))
                  ) || []
                }
                darkMode
              />
            </div>

            <div className={styles.submit}>
              <button
                disabled={props.invalid}
                className={styles.submitButton}
                type="submit"
              >
                {groupData.type === ModalType.add
                  ? t('site_group_management.modal_add.button_create')
                  : t('site_group_management.modal_edit.button_update')}
              </button>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};

export default CreateUpdateSiteGroupForm;
